import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { uploadMedia } from "../lib/uploadMedia";
import { generateTextVideoBlob } from "../lib/textToVideo";
import { useNavigate } from "react-router-dom";
import { UploadSimple, Sparkle, X, MapPin, Lock, Users, Globe, MusicNotes, TextT, Microphone, Stop, MagicWand, Waveform } from "@phosphor-icons/react";
import MusicPicker from "../components/MusicPicker";
import VoiceEffectsPanel from "../components/VoiceEffectsPanel";
import { installVoiceEnhancer } from "../lib/voiceEnhancer";

const VOICE_DROP_MAX_SEC = 15;

const PRIVACY_OPTIONS = [
  { value: "public",    label: "Public",    icon: Globe, sub: "Anyone can view" },
  { value: "followers", label: "Followers", icon: Users, sub: "Only people who follow you" },
  { value: "private",   label: "Only me",   icon: Lock,  sub: "Hidden from everyone else" },
];

export default function Create() {
  const nav = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [caption, setCaption] = useState("");
  const [location, setLocation] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [music, setMusic] = useState(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [err, setErr] = useState("");
  const [aiAudio, setAiAudio] = useState(null); // AI voice clip from /ai-voice (baked into text-videos)

  // Voice Drop — a short effect-processed voice intro attached to the post
  const [voiceDrop, setVoiceDrop] = useState(null); // { url, blob, seconds }
  const [vdRecording, setVdRecording] = useState(false);
  const [vdSeconds, setVdSeconds] = useState(0);
  const [showFx, setShowFx] = useState(false);
  const vdRecorderRef = useRef(null);
  const vdStreamRef = useRef(null);
  const vdChunksRef = useRef([]);
  const vdTimerRef = useRef(null);
  const vdSecondsRef = useRef(0);

  useEffect(() => () => stopVdStream(), []); // eslint-disable-line

  // Preselect a sound when arriving from Remix Sound Charts (/create?music=<id>)
  // or an AI voice clip from the AI Voice Studio (/create?ai_audio=<id>)
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const mid = qs.get("music");
    if (mid) api.get(`/music/${mid}`).then((r) => setMusic(r.data)).catch(() => {});
    const aid = qs.get("ai_audio");
    if (aid) api.get(`/ai/voice/audio/${aid}`).then((r) => { setAiAudio(r.data); if (!caption) setCaption(r.data.text || ""); }).catch(() => {});
  }, []); // eslint-disable-line

  const stopVdStream = () => {
    if (vdStreamRef.current) {
      vdStreamRef.current.getTracks().forEach((t) => t.stop());
      if (vdStreamRef.current.__rawStream) {
        vdStreamRef.current.__rawStream.getTracks().forEach((t) => t.stop());
      }
      vdStreamRef.current = null;
    }
    if (vdTimerRef.current) { clearInterval(vdTimerRef.current); vdTimerRef.current = null; }
  };

  const startVoiceDrop = async () => {
    try {
      installVoiceEnhancer(); // effects chain applies to this mic capture
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      vdStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      vdChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size > 0) vdChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(vdChunksRef.current, { type: mime });
        setVoiceDrop((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return { url: URL.createObjectURL(blob), blob, seconds: vdSecondsRef.current };
        });
        stopVdStream();
        setVdRecording(false);
      };
      rec.start();
      vdRecorderRef.current = rec;
      vdSecondsRef.current = 0;
      setVdSeconds(0);
      vdTimerRef.current = setInterval(() => {
        vdSecondsRef.current += 1;
        setVdSeconds(vdSecondsRef.current);
        if (vdSecondsRef.current >= VOICE_DROP_MAX_SEC) stopVoiceDrop();
      }, 1000);
      setVdRecording(true);
    } catch (e) {
      setErr("Microphone permission needed for a Voice Drop.");
    }
  };

  const stopVoiceDrop = () => {
    if (vdRecorderRef.current && vdRecorderRef.current.state !== "inactive") {
      vdRecorderRef.current.stop();
    }
  };

  const clearVoiceDrop = () => {
    if (voiceDrop?.url) URL.revokeObjectURL(voiceDrop.url);
    setVoiceDrop(null);
    setVdSeconds(0);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setMediaType(f.type.startsWith("video") ? "video" : "image");
  };

  const reset = () => {
    setFile(null); setPreview(null); setMediaType(null); setCaption(""); setLocation("");
    setPrivacy("public"); setErr(""); setProgress(0); clearVoiceDrop();
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/caption", { hint: caption || "energetic dance moment" });
      setCaption(data.caption);
    } catch (e) {
      setErr("AI caption failed. Try again.");
    } finally { setAiLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !caption.trim()) {
      setErr("Pick a photo/video or type a caption to turn into a video.");
      return;
    }
    setErr(""); setUploading(true); setProgress(0);
    try {
      // Text-only path: generate a 6-second vertical text video from the caption.
      let uploadFile = file;
      if (!uploadFile) {
        setGenerating(true);
        try {
          let voiceBuffer = null;
          if (aiAudio) {
            const AC = window.AudioContext || window.webkitAudioContext;
            const ctx = new AC();
            const res = await fetch(fileUrl(aiAudio.url), { credentials: "include" });
            voiceBuffer = await ctx.decodeAudioData(await res.arrayBuffer());
            ctx.close().catch(() => {});
          }
          const blob = await generateTextVideoBlob(caption, {
            duration: voiceBuffer ? Math.min(60, Math.max(6, Math.ceil(voiceBuffer.duration) + 1)) : 6,
            voiceBuffer,
            onProgress: ({ percent }) => setProgress(Math.min(30, Math.round(percent * 0.3))),
          });
          uploadFile = new File([blob], `text-video-${Date.now()}.webm`, { type: "video/webm" });
        } finally { setGenerating(false); }
      }
      const uploaded = await uploadMedia(uploadFile, {
        onProgress: (p) => setProgress(Math.max(30, p)),
      });
      const body = {
        caption,
        media_path: uploaded.path,
        media_type: uploaded.media_type,
        privacy,
        location: location.trim() || null,
        music_id: music?.id || null,
        ai_labels: aiAudio && !file ? ["ai_voice"] : undefined,
      };
      if (uploaded.mux) {
        body.mux_upload_id = uploaded.mux.upload_id;
        body.mux_passthrough = uploaded.mux.passthrough;
        body.mux_status = uploaded.mux.status;
      }
      if (voiceDrop?.blob) {
        const fd = new FormData();
        fd.append("file", voiceDrop.blob, "voicedrop.webm");
        fd.append("title", "Voice Drop");
        fd.append("kind", "recording");
        fd.append("is_public", "false");
        fd.append("duration_sec", String(voiceDrop.seconds || 0));
        const { data: vd } = await api.post("/voice/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        body.voice_drop_path = vd.storage_path;
        body.voice_drop_duration = voiceDrop.seconds || null;
      }
      await api.post("/posts", body);
      nav("/");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8">
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Create</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Drop your vibe</h1>
      <p className="text-cream/60 mt-2 text-sm">Photo, video, or just a caption — we'll turn text into a shareable video for you.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        {!preview ? (
          <button
            type="button"
            data-testid="create-upload-trigger"
            onClick={() => inputRef.current?.click()}
            className="w-full aspect-[9/14] max-h-[60vh] rounded-3xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-3 hover:border-papaya/60 hover:bg-white/5 transition-all"
          >
            <UploadSimple size={32} className="text-papaya" weight="duotone" />
            <p className="text-cream font-bold">Tap to upload</p>
            <p className="text-cream/50 text-xs tracking-[0.2em] uppercase">photo or video · max 50mb</p>
            <div className="flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-papaya/10 border border-papaya/30">
              <TextT size={14} className="text-papaya" weight="fill" />
              <span className="text-papaya text-[10px] tracking-[0.2em] uppercase font-black">
                or just type a caption — text becomes video
              </span>
            </div>
          </button>
        ) : (
          <div className="relative w-full aspect-[9/14] max-h-[60vh] rounded-3xl overflow-hidden bg-white/5">
            {mediaType === "video" ? (
              <video src={preview} className="w-full h-full object-cover" controls />
            ) : (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            )}
            <button type="button" onClick={reset} data-testid="create-clear" className="absolute top-3 right-3 w-9 h-9 rounded-full crystal-glass text-cream flex items-center justify-center">
              <X size={18} />
            </button>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*,video/*" onChange={onPick} className="hidden" data-testid="create-file-input" />

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Caption</span>
            <button
              type="button"
              data-testid="create-ai-caption"
              onClick={aiSuggest}
              disabled={aiLoading}
              className="flex items-center gap-1.5 text-xs font-bold text-papaya hover:text-papaya-hover transition-colors disabled:opacity-50"
            >
              <Sparkle size={14} weight="fill" />
              {aiLoading ? "Thinking…" : "AI suggest"}
            </button>
          </div>
          <textarea
            data-testid="create-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Say something island…"
            rows={3}
            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors resize-none"
          />
        </div>

        {aiAudio && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-papaya/10 border border-papaya/40" data-testid="create-ai-audio">
            <Sparkle size={16} className="text-papaya flex-shrink-0" weight="fill" />
            <div className="flex-1 min-w-0">
              <p className="text-cream font-bold text-sm truncate">AI voice · {aiAudio.title}</p>
              <p className="text-cream/50 text-[11px] truncate">
                {file ? "Voice-over an uploaded clip in AI Voiceover instead" : `${aiAudio.voice_name} · baked into your text video`}
              </p>
            </div>
            <audio src={fileUrl(aiAudio.url)} controls className="h-8 w-28" />
            <button type="button" onClick={() => setAiAudio(null)} data-testid="create-ai-audio-clear" className="text-cream/50 hover:text-cream flex-shrink-0"><X size={16} /></button>
          </div>
        )}

        <div>
          <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Music</span>
          <button
            type="button"
            data-testid="create-music-open"
            onClick={() => setMusicOpen(true)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors ${music ? "bg-papaya/10 border-papaya/40" : "bg-white/5 border-white/10 hover:border-white/20"}`}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-papaya/30 via-jam-green/20 to-jam-gold/20 flex items-center justify-center text-lg">
              {music ? music.cover_emoji : <MusicNotes size={16} className="text-papaya" weight="fill" />}
            </div>
            <div className="flex-1 min-w-0 text-left">
              {music ? (
                <>
                  <p className="text-cream font-bold text-sm truncate">{music.title}</p>
                  <p className="text-cream/50 text-[11px] truncate">{music.artist} · {music.mood}</p>
                </>
              ) : (
                <>
                  <p className="text-cream font-bold text-sm">Add a sound</p>
                  <p className="text-cream/50 text-[11px]">30 royalty-free tracks across soca, reggae, afro, hip-hop, lofi…</p>
                </>
              )}
            </div>
            {music && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setMusic(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") setMusic(null); }}
                data-testid="create-music-clear"
                className="text-cream/50 hover:text-cream cursor-pointer"
              >
                <X size={16} />
              </span>
            )}
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Voice Drop</span>
            <button
              type="button"
              data-testid="create-voice-fx"
              onClick={() => setShowFx(true)}
              className="flex items-center gap-1.5 text-xs font-bold text-papaya hover:text-papaya-hover transition-colors"
            >
              <MagicWand size={14} weight="fill" /> Voice FX
            </button>
          </div>
          {!voiceDrop ? (
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10">
              {!vdRecording ? (
                <button
                  type="button"
                  data-testid="create-voicedrop-record"
                  onClick={startVoiceDrop}
                  className="w-10 h-10 rounded-full bg-papaya text-ink flex items-center justify-center hover:brightness-110 flex-shrink-0"
                >
                  <Microphone size={18} weight="fill" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="create-voicedrop-stop"
                  onClick={stopVoiceDrop}
                  className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center animate-pulse flex-shrink-0"
                >
                  <Stop size={18} weight="fill" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-cream font-bold text-sm">
                  {vdRecording ? `Recording… ${vdSeconds}s` : "Record a voice intro"}
                </p>
                <p className="text-cream/50 text-[11px]">
                  {vdRecording
                    ? `Max ${VOICE_DROP_MAX_SEC}s — Robot, KTV & more via Voice FX`
                    : "Say it with a Robot voice or KTV reverb — plays on your post"}
                </p>
              </div>
              <Waveform size={20} className={vdRecording ? "text-red-400 animate-pulse" : "text-cream/30"} />
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-papaya/10 border border-papaya/40" data-testid="create-voicedrop-preview">
              <audio src={voiceDrop.url} controls className="flex-1 h-9" />
              <span className="text-cream/60 text-xs flex-shrink-0">{voiceDrop.seconds}s</span>
              <button
                type="button"
                data-testid="create-voicedrop-clear"
                onClick={clearVoiceDrop}
                className="text-cream/50 hover:text-cream flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        <div>
          <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Location</span>
          <div className="relative">
            <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-papaya" weight="fill" />
            <input
              data-testid="create-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Kingston, Jamaica"
              className="w-full bg-white/5 border border-white/10 rounded-2xl pl-11 pr-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors text-sm"
            />
          </div>
        </div>

        <div>
          <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Who can see this?</span>
          <div className="grid grid-cols-3 gap-2" data-testid="create-privacy">
            {PRIVACY_OPTIONS.map((opt) => {
              const I = opt.icon;
              const active = privacy === opt.value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  data-testid={`create-privacy-${opt.value}`}
                  onClick={() => setPrivacy(opt.value)}
                  className={`p-3 rounded-2xl text-left transition-colors ${active ? "bg-papaya/10 border-2 border-papaya text-cream" : "bg-white/5 border-2 border-transparent text-cream/60 hover:text-cream"}`}
                >
                  <I size={18} weight={active ? "fill" : "duotone"} className={active ? "text-papaya" : ""} />
                  <p className="font-black text-[12px] mt-2">{opt.label}</p>
                  <p className="text-[10px] mt-0.5 leading-tight opacity-70">{opt.sub}</p>
                </button>
              );
            })}
          </div>
        </div>

        {err && <p data-testid="create-error" className="text-red-400 text-sm">{err}</p>}

        {uploading && progress > 0 && progress < 100 && (
          <div data-testid="create-progress" className="space-y-2">
            <div className="flex justify-between text-xs text-cream/60 font-bold tracking-[0.15em] uppercase">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-papaya transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <button
          type="submit"
          data-testid="create-submit"
          disabled={uploading || (!file && !caption.trim())}
          className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors disabled:opacity-40"
        >
          {uploading
            ? (generating
                ? "Generating video from your text…"
                : (progress >= 100 ? "Finalizing…" : "Posting…"))
            : (!file && caption.trim() ? "Turn text into video · Post" : "Post to feed")}
        </button>
      </form>

      {musicOpen && (
        <MusicPicker
          selectedId={music?.id}
          onClose={() => setMusicOpen(false)}
          onSelect={(m) => { setMusic(m); setMusicOpen(false); }}
        />
      )}
      <VoiceEffectsPanel open={showFx} onClose={() => setShowFx(false)} />
    </div>
  );
}
